import { Link } from "react-router-dom";
import {
  ArrowRight,
  Download,
  CheckCircle2,
  XCircle,
  Play,
  ExternalLink,
  Layers,
  Building2,
  Users,
  Home,
  TrendingUp,
  BookOpen,
  ClipboardList,
  Wrench,
  Footprints,
  GraduationCap,
  DollarSign,
  Eye,
  Target,
  Zap,
  CheckCheck,
  Quote,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSiteSettings, mediaUrl } from "@/lib/siteSettings";

const DEFAULT_FLYER = "/cornerstone-coaches-guide.png";
const FLYER_FILENAME = "Cornerstone-Coaches-Guide.png";

const PILLARS = [
  { icon: Users, label: "Stronger Coaches" },
  { icon: Home, label: "Stronger Agents" },
  { icon: TrendingUp, label: "Stronger Market Centers" },
];

const WHATS_INSIDE = [
  {
    icon: BookOpen,
    number: "1",
    title: "The Foundation",
    points: [
      "How to build a successful coaching program",
      "What it should include",
      "Key components of a highly successful program",
      "The coaching process",
      "Sample contracts",
    ],
  },
  {
    icon: ClipboardList,
    number: "2",
    title: "The Coaching Experience",
    points: [
      "Coaching philosophy",
      "Key considerations for contract with agents",
      "Key considerations for contract between MC and coach",
      "Coaching models",
    ],
  },
  {
    icon: Wrench,
    number: "3",
    title: "Tools & Systems",
    points: [
      "12-Month Coaching Contest System",
      "12 Coaches Playbooks (Social, Video, AI Prompts)",
      "Dialogue Playbooks (1–4 Scenarios)",
      "Roadmaps & Maps",
      "Resource Library",
    ],
  },
  {
    icon: Footprints,
    number: "4",
    title: "Coaching the Agent",
    points: [
      "Onboarding & 4-Week Launch Plan",
      "30-60-90 Plan",
      "Weekly Coaching Cadence",
      "Mindset, Skillset, Activity & Growth",
      "Lead Generation & Conversion Systems",
    ],
  },
  {
    icon: GraduationCap,
    number: "5",
    title: "Training & Development",
    points: [
      "Certification Training",
      "Weekly Workshops",
      "Coaches Growth Plan",
      "Weekly Mastermind Calls",
    ],
  },
  {
    icon: DollarSign,
    number: "6",
    title: "Compensation & Recognition",
    points: [
      "Compensation Models (Self-Funded, Salary, Hybrid, Advanced Hybrid)",
      "Coach Income Examples",
      "Recruiting Bonus",
      "Non-Income Incentives",
    ],
  },
];

const MONTHLY_THEMES = [
  "Momentum Streak",
  "Loving on Listings",
  "Buyer Escape Room",
  "Listings on Purpose",
  "Pipeline Builder",
  "Open House Engine",
  "Peak Performer",
  "Referral Powerhouse",
  "AI Audit / Online Alignment",
  "November Gratitude",
  "Visionary Leader",
  "December Hustle",
];

const SYSTEM_STEPS = [
  { icon: Eye, label: "Awareness", desc: "Help agents see what's possible" },
  { icon: Target, label: "Clarity", desc: "Get clear on goals and a plan" },
  { icon: Zap, label: "Action", desc: "Take consistent daily steps" },
  { icon: CheckCheck, label: "Accountability", desc: "Track, adjust and celebrate progress" },
];

const WHO_ITS_FOR = ["Productivity Coaches", "Team Leaders", "Operating Principals"];
const WHO_ITS_NOT = [
  "Those looking for a quick fix",
  "Those not willing to take action",
  "Those not committed to growth",
];

const ENGAGE_TIERS = [
  {
    tag: "Basic",
    name: "Workbook",
    price: "$197",
    desc: "Essential tools and resources.",
  },
  {
    tag: "Plus",
    name: "Implementation Lab",
    price: "$997",
    subtitle: "6-Week Program",
    desc: "Live training, tools, and implementation.",
    featured: true,
  },
  {
    tag: "Premier",
    name: "Advanced Systems",
    price: "$1,997",
    desc: "Complete system, advanced training and support.",
  },
  {
    tag: "Community",
    name: "Community Access",
    price: "$39",
    priceSuffix: "/ month",
    desc: "Ongoing support, resources and community.",
  },
];

export default function LeadershipCoaching() {
  const { settings } = useSiteSettings();
  const flyerSrc = mediaUrl(settings?.images?.cornerstoneFlyerKey, DEFAULT_FLYER);
  return (
    <div data-testid="leadership-coaching-page">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-12">
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-primary">
              <Building2 size={14} /> The Cornerstone Advantage
            </span>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl mt-5 leading-[1.02]">
              A system.
              <br />
              A community.
              <br />
              <span className="italic text-primary">Real results.</span>
            </h1>
          </div>
          <p className="md:col-span-4 text-lg text-foreground/80 leading-relaxed">
            Helping Productivity Coaches get agents into production faster —
            with proven systems, tools, and accountability.
          </p>
        </div>

        {/* Three pillars */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {PILLARS.map((p) => (
            <div
              key={p.label}
              className="bg-card border border-border rounded-2xl p-6 flex items-center gap-4"
              data-testid={`pillar-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <p.icon className="text-primary" size={22} strokeWidth={1.6} />
              </div>
              <p className="font-serif text-xl md:text-2xl leading-tight">{p.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Flyer display + download */}
      <section className="bg-muted/40 py-16 md:py-20" data-testid="flyer-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-4">
            <span className="text-[11px] uppercase tracking-[0.3em] text-primary">
              The Coaches Guide
            </span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
              The full Cornerstone flyer.
            </h2>
            <p className="text-foreground/75 mt-5 leading-relaxed">
              Everything below at a glance — the six pillars, the 12-month
              contest system, the four-step process, and the three ways to
              engage. Save it, share it, print it.
            </p>
            <a
              href={flyerSrc}
              download={FLYER_FILENAME}
              data-testid="flyer-download-btn"
              className="inline-flex mt-8"
            >
              <Button className="rounded-full bg-primary hover:bg-primary/90 btn-lift h-12 px-7">
                <Download size={16} className="mr-2" />
                Download the guide
              </Button>
            </a>
            <p className="text-xs text-foreground/55 mt-4">
              PNG · 2 MB · optimized for print or digital
            </p>
          </div>
          <div className="md:col-span-8">
            <a
              href={flyerSrc}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="flyer-image-link"
              className="block bg-card border border-border rounded-2xl overflow-hidden shadow-sm card-lift"
            >
              <img
                src={flyerSrc}
                alt="Cornerstone Coaches Guide — The Cornerstone Advantage"
                className="w-full h-auto"
                data-testid="flyer-image"
                loading="lazy"
              />
            </a>
            <p className="text-xs text-foreground/55 mt-3 text-center">
              Click the image to open full-size in a new tab.
            </p>
          </div>
        </div>
      </section>

      {/* What's Inside */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24" data-testid="whats-inside-section">
        <div className="grid md:grid-cols-12 gap-10 mb-12 items-end">
          <div className="md:col-span-7">
            <span className="text-[11px] uppercase tracking-[0.3em] text-primary">
              What's Inside
            </span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
              Six pillars of a coaching program that actually runs.
            </h2>
          </div>
          <p className="md:col-span-4 md:col-start-9 text-foreground/75 leading-relaxed">
            Each section is a complete module — from the foundation up
            through how you get paid for the work.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {WHATS_INSIDE.map((s, i) => (
            <div
              key={s.title}
              className="bg-card border border-border rounded-2xl p-7 flex flex-col"
              data-testid={`inside-${i + 1}`}
            >
              <div className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-serif text-lg">
                  {s.number}
                </span>
                <s.icon className="text-primary" size={22} strokeWidth={1.6} />
              </div>
              <h3 className="font-serif text-2xl mt-5 leading-tight">{s.title}</h3>
              <ul className="mt-4 space-y-2.5 text-sm text-foreground/75 leading-relaxed">
                {s.points.map((p) => (
                  <li key={p} className="flex gap-2.5 items-start">
                    <span className="text-primary shrink-0 mt-2 h-1 w-1 rounded-full bg-primary" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* 12-Month Contest System + System in Action */}
      <section className="bg-[#0F1E33] text-[#F5F8FB] py-20 md:py-24" data-testid="system-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-6">
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#93C5FD]">
              12-Month Coaching Contest System
            </span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
              Monthly themes that build habits, skills, and results.
            </h2>
            <p className="opacity-85 mt-5 leading-relaxed">
              One theme per month — a full year of momentum designed to
              build the muscle memory of a real business.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-2.5">
              {MONTHLY_THEMES.map((t, i) => (
                <div
                  key={t}
                  className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm flex gap-3 items-center"
                  data-testid={`month-${i + 1}`}
                >
                  <span className="text-[#93C5FD] text-xs font-mono">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span>{t}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-6">
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#93C5FD]">
              The Cornerstone System in Action
            </span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
              A simple process that creates powerful results.
            </h2>
            <div className="mt-10 space-y-3">
              {SYSTEM_STEPS.map((s, i) => (
                <div
                  key={s.label}
                  className="flex gap-5 items-start bg-white/5 border border-white/10 rounded-2xl p-5"
                  data-testid={`step-${i + 1}`}
                >
                  <div className="w-14 h-14 shrink-0 rounded-full bg-[#93C5FD]/20 flex items-center justify-center">
                    <s.icon className="text-[#93C5FD]" size={24} strokeWidth={1.7} />
                  </div>
                  <div>
                    <div className="flex items-center gap-3">
                      <p className="font-serif text-2xl">{s.label}</p>
                      <span className="text-[#93C5FD] text-xs font-mono">
                        step {i + 1} of 4
                      </span>
                    </div>
                    <p className="text-sm opacity-85 mt-1 leading-relaxed">
                      {s.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Who it's for / Who it's not for */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24">
        <div className="grid md:grid-cols-2 gap-6">
          <div
            className="bg-card border border-border rounded-2xl p-8"
            data-testid="who-its-for"
          >
            <span className="text-[11px] uppercase tracking-[0.3em] text-primary">
              Who it's for
            </span>
            <h2 className="font-serif text-3xl md:text-4xl mt-4 leading-tight">
              Built for leaders who coach.
            </h2>
            <ul className="mt-8 space-y-4">
              {WHO_ITS_FOR.map((w) => (
                <li key={w} className="flex gap-3 items-start text-foreground/85">
                  <CheckCircle2 className="text-primary shrink-0 mt-0.5" size={20} strokeWidth={1.7} />
                  <span className="text-lg">{w}</span>
                </li>
              ))}
            </ul>
          </div>

          <div
            className="bg-muted/50 border border-border rounded-2xl p-8"
            data-testid="who-its-not"
          >
            <span className="text-[11px] uppercase tracking-[0.3em] text-foreground/60">
              Who it's not for
            </span>
            <h2 className="font-serif text-3xl md:text-4xl mt-4 leading-tight text-foreground/80">
              Honest about the fit.
            </h2>
            <ul className="mt-8 space-y-4">
              {WHO_ITS_NOT.map((w) => (
                <li key={w} className="flex gap-3 items-start text-foreground/70">
                  <XCircle className="text-foreground/45 shrink-0 mt-0.5" size={20} strokeWidth={1.7} />
                  <span className="text-lg">{w}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Pull-quote */}
      <section className="bg-muted/40 py-20 md:py-24" data-testid="pull-quote-section">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <Quote className="text-primary mx-auto" size={36} strokeWidth={1.5} />
          <p className="font-serif text-3xl md:text-4xl italic mt-6 leading-snug text-foreground">
            "When coaches are equipped, agents are empowered, and market
            centers grow."
          </p>
          <p className="font-serif text-xl md:text-2xl mt-8 text-foreground/85 max-w-2xl mx-auto leading-snug">
            We build better coaches so you can build better businesses — and
            better lives.
          </p>
        </div>
      </section>

      {/* 3 Ways to Engage */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24" data-testid="engage-section">
        <div className="grid md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-7">
            <span className="text-[11px] uppercase tracking-[0.3em] text-primary">
              Ways to engage
            </span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">
              Four ways to bring Cornerstone into your business.
            </h2>
          </div>
          <p className="md:col-span-4 md:col-start-9 text-foreground/75 leading-relaxed">
            From a self-serve workbook to the complete premier system —
            start where you are, grow into what's next.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {ENGAGE_TIERS.map((t, i) => (
            <div
              key={t.name}
              data-testid={`tier-${t.tag.toLowerCase()}`}
              className={`rounded-2xl p-7 flex flex-col ${
                t.featured
                  ? "bg-[#0F1E33] text-[#F5F8FB] border border-[#1E3A8A]"
                  : "bg-card border border-border"
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <span
                  className={`text-[10px] uppercase tracking-[0.22em] px-2.5 py-1 rounded-full ${
                    t.featured
                      ? "bg-[#93C5FD]/20 text-[#93C5FD]"
                      : "bg-muted text-foreground/60"
                  }`}
                >
                  {t.tag}
                </span>
                {t.featured && (
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[#93C5FD]">
                    Most Popular
                  </span>
                )}
              </div>
              <h3
                className={`font-serif text-2xl leading-tight ${
                  t.featured ? "text-[#F5F8FB]" : "text-foreground"
                }`}
              >
                {t.name}
              </h3>
              {t.subtitle && (
                <p
                  className={`text-xs uppercase tracking-[0.18em] mt-1 ${
                    t.featured ? "text-[#93C5FD]" : "text-primary"
                  }`}
                >
                  {t.subtitle}
                </p>
              )}
              <div className="mt-5 flex items-baseline gap-1">
                <span
                  className={`font-serif text-4xl ${
                    t.featured ? "text-[#F5F8FB]" : "text-foreground"
                  }`}
                >
                  {t.price}
                </span>
                {t.priceSuffix && (
                  <span
                    className={`text-sm ${
                      t.featured ? "text-[#F5F8FB]/70" : "text-foreground/60"
                    }`}
                  >
                    {t.priceSuffix}
                  </span>
                )}
              </div>
              <p
                className={`mt-4 text-sm leading-relaxed flex-1 ${
                  t.featured ? "opacity-85" : "text-foreground/70"
                }`}
              >
                {t.desc}
              </p>
              <Link
                to="/contact"
                data-testid={`tier-cta-${i}`}
                className="mt-6"
              >
                <Button
                  variant={t.featured ? "default" : "outline"}
                  className={`w-full rounded-full h-11 ${
                    t.featured
                      ? "bg-[#93C5FD] hover:bg-[#BAE6FD] text-[#0F1E33]"
                      : "border-border"
                  }`}
                >
                  {t.featured ? "Start with Plus" : "Get started"}
                </Button>
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Cornerstone recordings library — kept from the previous page */}
      <section
        className="bg-muted/30 py-20 md:py-24 border-y border-border"
        data-testid="cornerstone-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
              <Layers className="text-primary" size={20} strokeWidth={1.6} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.28em] text-primary">
                Cornerstone Advantage
              </p>
              <p className="font-serif text-base leading-tight text-foreground">
                Recordings from Tammy
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-7" data-testid="cornerstone-video-overview-wrap">
              <CornerstoneVideo
                title="Cornerstone Advantage — Program Overview"
                eyebrow="Coaching Systems"
                badge="Featured Recording"
                desc="Tammy walks through the Cornerstone Advantage system, who it's built for, and how it works in real practice."
                youtubeId="RP8ehYzx4Nw"
                externalUrl="https://us06web.zoom.us/rec/share/NNVaQLj6zM0HJ0tLDMVRYR9Q9VvqhqSymaqhNYV_peOfgns0s_DsLH-d8iCEpKak.R_0pMJvPjU6_rSKr"
                size="large"
                testid="cornerstone-video-overview"
              />
            </div>
            <div className="md:col-span-5 space-y-5">
              <CornerstoneVideo
                title="Coaching In & For Leadership Roles for Profitability"
                eyebrow="Leadership & Profitability"
                badge="Leadership Track"
                desc="What it takes to lead with profitability — for team leaders and market center leaders."
                youtubeId="dZZ4_lgi6HQ"
                externalUrl="https://us06web.zoom.us/rec/share/1gUB9aVzmfRbK58ccPwK99lUlXwh39xcG13EX2Dowoe63WK2eY2IAtHacMiIHSwM.BkEi1quqMEyhiVpU"
                size="small"
                testid="cornerstone-video-leadership"
              />
              <CornerstoneVideo
                title="Why Coaches Struggle"
                eyebrow="Coaching for Coaches"
                badge="Coaches Track"
                desc="The patterns Tammy sees again and again in coaches who plateau — and what to do about each one."
                youtubeId="jIYuS5gDt-I"
                size="small"
                testid="cornerstone-video-why-coaches-struggle"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#0F1E33] text-[#F5F8FB]" data-testid="leadership-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <span className="inline-block text-[11px] uppercase tracking-[0.3em] text-[#93C5FD] mb-4">
              Ready to build your advantage?
            </span>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">
              You don't have to reinvent the wheel.
              <br />
              <span className="italic text-[#93C5FD]">We've already built it for you.</span>
            </h2>
            <p className="mt-6 text-lg opacity-85 leading-relaxed max-w-xl">
              Let's create a coaching culture that produces results — tell
              us about your market center or team and Tammy will respond
              personally.
            </p>
          </div>
          <div className="md:col-span-5 md:text-right flex md:justify-end flex-wrap gap-4">
            <a
              href={flyerSrc}
              download={FLYER_FILENAME}
              data-testid="leadership-cta-download"
            >
              <Button
                variant="outline"
                className="rounded-full border-[#93C5FD]/40 bg-transparent text-[#F5F8FB] hover:bg-white/10 h-12 px-7"
              >
                <Download size={16} className="mr-2" />
                Download flyer
              </Button>
            </a>
            <Link to="/contact" data-testid="leadership-cta-contact">
              <Button className="rounded-full bg-[#93C5FD] hover:bg-[#BAE6FD] text-[#0F1E33] btn-lift h-12 px-8 text-base">
                Start the conversation <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
        <div className="border-t border-white/10">
          <p className="max-w-7xl mx-auto px-6 md:px-10 py-6 text-xs uppercase tracking-[0.3em] text-[#93C5FD] text-center">
            Cornerstone Coaches Guide · Equip. Empower. Elevate.
          </p>
        </div>
      </section>
    </div>
  );
}

/* ----- Reusable video card ----- */
function CornerstoneVideo({
  title, eyebrow, badge, desc,
  youtubeId, externalUrl, size = "large", testid,
}) {
  const isLarge = size === "large";
  const playSize = isLarge ? "w-20 h-20" : "w-16 h-16";
  const iconSize = isLarge ? 28 : 22;

  if (youtubeId) {
    const isSmall = size === "small";
    return (
      <div
        data-testid={testid}
        className="rounded-2xl overflow-hidden bg-card border border-border shadow-sm flex flex-col"
      >
        <div className="relative aspect-video bg-black">
          <iframe
            src={`https://www.youtube.com/embed/${youtubeId}?rel=0`}
            title={title}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
        <div className={isSmall ? "p-5" : "p-7"}>
          <p className="text-[10px] uppercase tracking-[0.22em] text-primary">{eyebrow}</p>
          <h3 className={`font-serif ${isSmall ? "text-lg" : "text-2xl md:text-3xl"} mt-2 leading-tight text-foreground`}>{title}</h3>
          {desc && <p className={`${isSmall ? "text-xs" : "text-sm"} text-foreground/70 mt-3 leading-relaxed`}>{desc}</p>}
        </div>
      </div>
    );
  }

  return (
    <a
      href={externalUrl}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      className="group block rounded-2xl overflow-hidden bg-card border border-border hover:border-primary/40 transition shadow-sm"
    >
      <div className="relative aspect-video bg-gradient-to-br from-primary/12 via-primary/8 to-primary/5">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className={`${playSize} rounded-full bg-primary flex items-center justify-center shadow-xl group-hover:scale-110 transition`}>
            <Play className="text-white ml-1" size={iconSize} fill="currentColor" />
          </div>
        </div>
        {badge && (
          <div className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.22em] text-primary bg-white/85 backdrop-blur border border-primary/15 px-3 py-1.5 rounded-full">
            {badge}
          </div>
        )}
        <div className="absolute bottom-4 right-4 text-[10px] uppercase tracking-[0.2em] text-foreground/70 bg-white/85 backdrop-blur border border-border px-3 py-1.5 rounded-full inline-flex items-center gap-1.5">
          Zoom <ExternalLink size={10} />
        </div>
      </div>
      <div className="p-7">
        <p className="text-[10px] uppercase tracking-[0.22em] text-primary">{eyebrow}</p>
        <h3 className={`font-serif ${isLarge ? "text-2xl md:text-3xl" : "text-2xl"} mt-2 leading-tight text-foreground`}>{title}</h3>
        {desc && <p className="text-sm text-foreground/70 mt-3 leading-relaxed max-w-2xl">{desc}</p>}
        <span className="inline-flex items-center gap-2 mt-5 text-sm text-primary font-medium group-hover:gap-3 transition-all">
          Watch the recording <ArrowRight size={14} />
        </span>
      </div>
    </a>
  );
}
